import { CALC_OPERATIONS } from "../constants";

/**
 * Finds a node in the AG Grid by its tag property
 * @param {Object} api - The AG Grid API
 * @param {string} tag - The tag to search for
 * @returns {Object|undefined} The found node or undefined if not found
 */
const findNodeByTag = (api, tag) =>
  api.getRenderedNodes().find((node) => node.data && node.data.tag === tag);

/**
 * Calculates the value for a specific month based on related tags using the specified operation
 * @param {Object} params - The cell render parameters from AG Grid
 * @param {string|number} month - The month to calculate the value for
 * @param {string} operation - The operation to perform (multiply or sum)
 * @returns {number|null} The calculated value or null if calculation not possible
 */
export const calculateMonthValueByTags = (params, month, operation) => {
  const { data, api } = params;
  const tags =
    operation === CALC_OPERATIONS.MULTIPLY ? data.multiply_tags : data.sum_tags;

  if (!tags || tags.length === 0) return null;

  const initialValue = operation === CALC_OPERATIONS.MULTIPLY ? 1 : 0;
  let total = initialValue;

  for (const tag of tags) {
    const node = findNodeByTag(api, tag);

    if (!node?.data?.months?.[month]) {
      // For multiplication, any null value makes the entire result null
      if (operation === CALC_OPERATIONS.MULTIPLY) return null;
      // For sum, we just skip this value
      continue;
    }

    // Get the column definition for the month column
    const col = api.getColumn(`months.${month}`);
    let cellValue = node.data.months[month];

    if (col) {
      const colDef = col.getColDef();
      if (colDef && typeof colDef.valueFormatter === "function") {
        // Call the valueFormatter manually to get the formatted value
        // instead of the raw value which is not up to date and useless
        const formatted = colDef.valueFormatter({
          value: node.data.months[month],
          data: node.data,
          node,
          colDef,
          column: col,
          api,
        });
        // Convert to number if possible; otherwise use raw value
        cellValue = Number(formatted) || cellValue;
      }
    }

    if (operation === CALC_OPERATIONS.MULTIPLY) {
      total *= cellValue;
    } else {
      total += cellValue;
    }
  }

  return total;
};

/**
 * Calculates the value for a specific month by multiplying values from related tags
 * @param {Object} params - The cell render parameters from AG Grid
 * @param {string|number} month - The month to calculate the value for
 * @returns {number|null} The calculated product or null if calculation not possible
 */
export const calculateMonthValueForMultiplyTags = (params, month) => {
  return calculateMonthValueByTags(params, month, CALC_OPERATIONS.MULTIPLY);
};

/**
 * Calculates the value for a specific month by summing values from related tags
 * @param {Object} params - The cell render parameters from AG Grid
 * @param {string|number} month - The month to calculate the value for
 * @returns {number|null} The calculated sum or null if no values to sum
 */
export const calculateMonthValueForSumTags = (params, month) => {
  return calculateMonthValueByTags(params, month, CALC_OPERATIONS.SUM);
};

/**
 * Determines the appropriate calculation method based on data configuration and calculates month value
 * @param {Object} params - The cell render parameters from AG Grid
 * @param {string|number} month - The month to calculate the value for
 * @returns {number|null} The calculated value or null if no calculation needed
 */
export const calculateMonthValue = (params, month) => {
  const { data } = params;

  if (data.multiply_tags?.length > 0) {
    return calculateMonthValueForMultiplyTags(params, month);
  }

  if (data.sum_tags?.length > 0) {
    return calculateMonthValueForSumTags(params, month);
  }

  return null;
};

/**
 * Refreshes all cells in the grid when a value changes that might affect dependent cells
 * @param {Object} params - The cell render parameters from AG Grid
 * @param {string|number} currentMonth - The month that was modified
 */
export const refreshDependentCells = (params, currentMonth) => {
  const { data, api, node } = params;
  if (!data?.tag) return;
  // Refresh all cells

  api.refreshCells({
    force: true,
  });
};

/**
 * Renders a month cell with appropriate formatting and calculated values
 * @param {Object} params - The cell render parameters from AG Grid
 * @param {boolean} addModifiedFormat - Whether to add custom formatting (e.g., tons)
 * @returns {string|number} The formatted value to display in the cell
 */
export const renderMonthCell = (params, addModifiedFormat = false) => {
  if (!params.data || params.value === undefined || params.value === null) {
    return "";
  }

  if (params.data?.format_to_ton && addModifiedFormat) {
    return `${params.value} T`;
  }

  const field = params.colDef.field;
  const month = parseInt(field.replace("months.", ""));

  if (
    params.data.multiply_tags?.length > 0 ||
    params.data.sum_tags?.length > 0
  ) {
    const calculatedValue = calculateMonthValue(params, month);
    return calculatedValue !== null ? calculatedValue : "";
  }

  return params.value;
};
